import { useMemo, useState, useCallback, useEffect } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button } from "reactstrap"

// Custom Components
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Hooks
import {
  useVehicleMovements,
  useDeleteVehicleMovement,
} from "hooks/useVehicleMovements"
import { useListParams } from "hooks/useListParams"

// Utils
import { formatReadableDate } from "utils/formatDate"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { generateStockTitle } from "helpers/stock_helpers"
import { Link } from "react-router-dom"
import VehicleMovementFilter from "./VehicleMovementFilter"
import { VehicleMovementListHeader } from "./VehicleMovementListHeader"
import VehicleMovementAddEdit from "./VehicleMovementAddEdit"
import { useResetParamsOnTabChange } from "hooks/useResetParamsOnTabChange"

const VehicleMovementList = () => {
  document.title = "Vehicle Movement List"

  // reset url params except 'tab', 'page'
    useResetParamsOnTabChange("movement-list")

  // State
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [movement, setMovement] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  // URL search params hook
  const {
    params,
    setPage,
    setSearch,
    resetParams,
    updateParams,
    hasActiveFilters,
  } = useListParams({
    page: 1,
    search: "",
    movement_type_id: "",
    location_to_id: "",
    movement_by_id: "",
    date_from: "",
    date_to: "",
  })

  // Query params
  const queryParams = useMemo(() => {
    const {
      page = 1,
      per_page = 10,
      search,
      movement_type_id,
      location_to_id,
      movement_by_id,
      date_from,
      date_to,
    } = params

    const q = { page, per_page }

    q.search = search
    q.movement_type_id = movement_type_id
    q.location_to_id = location_to_id
    q.movement_by_id = movement_by_id
    q.date_from = date_from
    q.date_to = date_to

    return q
  }, [
    params.page,
    params.search,
    params.movement_type_id,
    params.location_to_id,
    params.movement_by_id,
    params.date_from,
    params.date_to,
  ])

  // React Query hooks
  const {
    data: movementsResponse,
    isLoading,
    refetch: refetchMovements,
    isRefetching,
  } = useVehicleMovements(queryParams, params?.tab === "movement-list")

  const { mutateAsync: deleteMovement, isPending: deleteLoading } =
    useDeleteVehicleMovement()

  // Options hook
  const { isLoading: optionLoading, raw: rawData } = useFilteredVehicleOptions()

  // Memoize the data to prevent unnecessary re-renders
  const movementTypesData = useMemo(
    () => rawData?.movement_types ?? [],
    [rawData?.movement_types]
  )

  const locationsData = useMemo(
    () => rawData?.locations ?? [],
    [rawData?.locations]
  )

  const employeesData = useMemo(
    () => rawData?.employees ?? [],
    [rawData?.employees]
  )

  // Toggle modal
  const toggleModal = useCallback(() => {
    if (modal) {
      setModal(false)
      setMovement(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setMovement(null)
    setIsEdit(false)
    toggleModal()
  }, [toggleModal])

  const handleEditClick = useCallback(
    item => {
      setMovement(item)
      setIsEdit(true)
      toggleModal()
    },
    [toggleModal]
  )

  const handleDeleteClick = useCallback(item => {
    setMovement(item)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!movement?.id) return
    try {
      const res = await deleteMovement(movement.id)
      toast.success(res?.message ?? "Vehicle movement deleted successfully.")
      setDeleteModal(false)
    } catch (error) {
      console.error(error)
      toast.error("Failed to delete vehicle movement.")
    }
  }, [movement, deleteMovement])

  const handleRefresh = () => refetchMovements()

  // Table columns
  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      {
        header: "Vehicle",
        accessorKey: "stock_id",
        cell: ({ row }) => {
          const title = generateStockTitle(row.original.vehicle)
          const color = row.original?.vehicle?.exterior_color?.name ?? ""
          return (
            <div style={{ maxWidth: 100 }} className="text-wrap">
              <div>
                {title || "-"} {color || ""}
              </div>
            </div>
          )
        },
      },
      {
        header: "Chassis No",
        accessorKey: "chassis",
        cell: ({ row }) => {
          return (
            <div>
              <div>{row.original.vehicle?.full_chassis || "-"}</div>
              <div>
                <Link
                  to={`/stock-list/${
                    row.original.vehicle?.stock_id || "#"
                  }/view`}
                >
                  {row.original.vehicle?.stock_id || "-"}
                </Link>
              </div>
            </div>
          )
        },
      },
      {
        header: "Movement Info",
        accessorKey: "locationFrom",
        cell: ({ row }) => {
          const from = row.original.location_from?.name || "-"
          const to = row.original.location_to?.name || "-"
          const movementType = row.original?.movement_type?.name || "-"
          const movementBy = row.original?.movement_by?.name || "-"
          const mileage = `${row.original?.mileage?.toLocaleString() || 0} km`
          const date = formatReadableDate(row.original?.movement_date)
          return (
            <div className="d-flex">
              <div className="flex-grow-1">
                <div className="mb-1">
                  <strong>{movementType}</strong>
                </div>
                <div className="text-muted mb-2">
                  <i className="bx bx-calendar me-1 text-secondary"></i>
                  {date}
                </div>

                <div className="d-flex flex-column gap-1">
                  <div className="d-flex align-items-center">
                    <i className="bx bx-map-pin me-1 text-danger"></i>
                    <span>
                      <strong>From:</strong> {from}
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="bx bx-map-pin me-1 text-success"></i>
                    <span>
                      <strong>To:</strong> {to}
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="bx bx-user me-1 text-info"></i>
                    <span>
                      <strong>By:</strong> {movementBy}
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="bx bx-tachometer me-1 text-warning"></i>
                    <span>
                      <strong>Last Mileage:</strong> {mileage}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )
        },
      },
      {
        header: "Remarks",
        accessorKey: "remarks",
        cell: ({ row }) => row.original.remarks || "-",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <Button
                color="info"
                size="sm"
                onClick={() => handleEditClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </Button>
            </li>
            <li>
              <Button
                color="danger"
                size="sm"
                onClick={() => handleDeleteClick(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </Button>
            </li>
          </ul>
        ),
      },
    ],
    [handleEditClick, handleDeleteClick]
  )

  const data = movementsResponse?.data?.data || []
  const paginationInfo = movementsResponse?.data

  return (
    <>
      <VehicleMovementListHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={handleRefresh}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <div className="pb-3"></div>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="mb-3">
        <VehicleMovementFilter
          filters={params}
          setFilters={updateParams}
          movementTypes={movementTypesData?.map(item => ({
            label: item?.name,
            value: item?.id,
          }))}
          movementByOptions={employeesData?.map(item => ({
            label: item?.name,
            value: item?.id,
          }))}
          locationOptions={locationsData?.map(item => ({
            label: item?.name,
            value: item?.id,
          }))}
          loading={optionLoading}
        />
      </div>

      <Row>
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>
      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      {/* Modal for Add/Edit */}
      <VehicleMovementAddEdit
        modal={modal}
        isEdit={isEdit}
        movement={movement}
        optionLoading={optionLoading}
        toggleModal={toggleModal}
        employeesData={employeesData}
        movementTypesData={movementTypesData}
        locationsData={locationsData}
      />
    </>
  )
}

export default VehicleMovementList
